import uPlot, { AlignedData } from 'uplot';
import { addOKLCHOpacity, getChartDimensions, setupChartObservers } from './lib/plot-utils';

function sumSquaredDistance(c: number, xs: number[]): number {
  return xs.map((x) => (c - x) ** 2).reduce((a, b) => a + b)
}

function mean(xs: number[]): number {
  return xs.reduce((a, b) => a + b) / xs.length;
}

function sumAbsDistance(c: number, xs: number[]): number {
  return xs.map((x) => Math.abs(c - x)).reduce((a, b) => a + b)
}

/**
 * @returns the [min, max] range of valid median values
 */
function median(xs: number[]): [number, number] {
  const nums = [...xs];
  nums.sort((a, b) => a - b);
  if (nums.length == 0) {
    // edge case to not access -1th element
    return [0, 0];
  }
  else if (nums.length % 2 == 1) {
    // for odd nums, median is exactly the middle element
    const m = nums[Math.floor(nums.length / 2)];
    return [m, m];
  } else {
    // for even nums, median is between the two middle elements
    const low = nums[nums.length / 2 - 1];
    const high = nums[nums.length / 2];
    return [low, high];
  }
}

// Shared drag handling functionality
interface DragHandlerConfig {
  uPlotInstance: uPlot;
  draggablePoints: number[];
  draggedPointIndex: { current: number | null };
  pointRadius: number;
  xMin: number;
  xMax: number;
  dragBuffer: number;
  onPointsChanged: (points: number[]) => void;
}

function setupDragHandlers(config: DragHandlerConfig) {
  const { uPlotInstance: u, draggablePoints, draggedPointIndex, pointRadius, xMin, xMax, dragBuffer, onPointsChanged } = config;
  const over = u.over;

  function getPointAtPosition(mouseX: number, mouseY: number): number | null {
    const yZero = u.valToPos(0, 'y', false);
    const verticalDistance = Math.abs(mouseY - yZero);
    if (verticalDistance > pointRadius + 5) {
      return null;
    }

    for (let i = 0; i < draggablePoints.length; i++) {
      const pointX = u.valToPos(draggablePoints[i], 'x', false);
      const horizontalDistance = Math.abs(mouseX - pointX);

      if (horizontalDistance <= pointRadius + 5) {
        return i;
      }
    }

    return null;
  }

  function updatePointPosition(clientX: number) {
    const rect = over.getBoundingClientRect();
    const mouseX = clientX - rect.left;

    const newXVal = u.posToVal(mouseX, 'x');
    const clampedX = Math.max(xMin + dragBuffer, Math.min(xMax - dragBuffer, newXVal));
    draggablePoints[draggedPointIndex.current!] = clampedX;

    onPointsChanged(draggablePoints);
  }

  // Mouse event handlers
  function onDocumentMouseMove(e: MouseEvent) {
    updatePointPosition(e.clientX);
  }

  function onDocumentMouseUp() {
    if (draggedPointIndex.current !== null) {
      draggedPointIndex.current = null;
      over.style.cursor = 'default';

      document.removeEventListener('mousemove', onDocumentMouseMove);
      document.removeEventListener('mouseup', onDocumentMouseUp);
    }
  }

  function onMouseDown(e: MouseEvent) {
    const rect = over.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const pointIndex = getPointAtPosition(mouseX, mouseY);
    if (pointIndex !== null) {
      draggedPointIndex.current = pointIndex;
      over.style.cursor = 'grabbing';
      e.preventDefault();

      document.addEventListener('mousemove', onDocumentMouseMove);
      document.addEventListener('mouseup', onDocumentMouseUp);
    }
  }

  function onMouseMove(e: MouseEvent) {
    if (draggedPointIndex.current === null) {
      const rect = over.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const pointIndex = getPointAtPosition(mouseX, mouseY);
      over.style.cursor = pointIndex !== null ? 'grab' : 'default';
    }
  }

  // Touch event handlers
  function onDocumentTouchMove(e: TouchEvent) {
    if (e.touches.length > 0) {
      updatePointPosition(e.touches[0].clientX);
    }
  }

  function onDocumentTouchEnd() {
    if (draggedPointIndex.current !== null) {
      draggedPointIndex.current = null;

      document.removeEventListener('touchmove', onDocumentTouchMove);
      document.removeEventListener('touchend', onDocumentTouchEnd);
      document.removeEventListener('touchcancel', onDocumentTouchEnd);
    }
  }

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length === 1) {
      const rect = over.getBoundingClientRect();
      const touch = e.touches[0];
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;

      const pointIndex = getPointAtPosition(touchX, touchY);
      if (pointIndex !== null) {
        draggedPointIndex.current = pointIndex;
        e.preventDefault();

        document.addEventListener('touchmove', onDocumentTouchMove, { passive: false });
        document.addEventListener('touchend', onDocumentTouchEnd);
        document.addEventListener('touchcancel', onDocumentTouchEnd);
      }
    }
  }

  over.addEventListener('mousedown', onMouseDown);
  over.addEventListener('mousemove', onMouseMove);
  over.addEventListener('touchstart', onTouchStart, { passive: false });
}


// Mean plot implementation
function initMeanChart() {
  // Plot-specific constants
  const Y_MIN = -50;
  const SAMPLE_STEP = 0.1;
  const X_MIN = -10.5;
  const X_MAX = 10.5;
  const DRAG_BUFFER = 0.5;
  const POINT_RADIUS = 8;
  const MIN_HEIGHT = 250;
  const ASPECT_RATIO = 16 / 9;

  // Draggable points state - odd number for mean
  const draggablePoints = [-3, 0, 2, 5, 6];
  const draggedPointIndex = { current: null as number | null };
  let meanLegendValue: HTMLTableCellElement | null = null;

  function generateChartData(points: number[]): AlignedData {
    const xValues: number[] = [];
    const yValues: number[] = [];
    const dataPointValues: (number | null)[] = [];

    for (let x = X_MIN; x <= X_MAX; x += SAMPLE_STEP) {
      xValues.push(x);
      const yVal = sumSquaredDistance(x, points);
      yValues.push(yVal);

      const isDataPoint = points.some(dp => Math.abs(x - dp) < SAMPLE_STEP / 2);
      dataPointValues.push(isDataPoint ? 0 : null);
    }

    return [xValues, yValues, dataPointValues];
  }

  let data = generateChartData(draggablePoints);

  function initLegendMean(u: uPlot, points: number[]) {
    // find the legend table body
    const legendTBody = u.root.querySelector('table.u-legend tbody');
    if (legendTBody == null) {
      console.warn("Couldn't find legend table body for manual addition.");
      return;
    }

    // we'll build a <tr> similar to existing legend. we use custom CSS classes defensibly, though
    // they still seem to get the correct uPlot styling applied (which is what we want).
    //
    // normal row:
    // <tr class="u-series">
    //     <th>
    //         <div class="u-marker" style="border: 2px solid oklch(0.577 0.245 27.325);"></div>
    //         <div class="u-label">Data</div>
    //     </th>
    //     <td class="u-value">42</td>
    // </tr>
    //
    // we build:
    // <tr class="u-legend-series">
    //     <th>
    //         <div class="u-legend-marker w-2 h-lh border-orange-400 border-l-3 border-dashed"></div>
    //         <div class="u-legend-label">Mean</div>
    //     </th>
    //     <td class="u-legend-value">2.00</td>
    // </tr>

    // row
    const tr = document.createElement('tr');
    tr.className = 'u-legend-series';

    // first cell is the header (<th>), with marker and label <div>s
    const th = document.createElement('th');
    const markerDiv = document.createElement('div');
    markerDiv.className = 'u-legend-marker w-2 h-lh border-(--chart-color-orange) border-l-2 border-dashed';
    th.appendChild(markerDiv);
    const labelDiv = document.createElement('div');
    labelDiv.className = 'u-legend-label';
    labelDiv.textContent = 'Mean';
    th.appendChild(labelDiv);
    tr.appendChild(th);

    // then, the value comes in a <td>
    const td = document.createElement('td');
    td.className = 'u-legend-value';
    td.textContent = '—'; // initial placeholder
    tr.appendChild(td);

    // append row and store reference to the value cell for updates
    legendTBody.appendChild(tr);
    meanLegendValue = td;
  }

  function drawLegendMean(_u: uPlot, points: number[]) {
    // update the value
    if (meanLegendValue) {
      meanLegendValue.textContent = mean(points).toFixed(2);
    }
  }


  function createChartOptions(width: number, height: number): uPlot.Options {
    const docStyle = window.getComputedStyle(document.documentElement);
    const squaredDifferenceLineColor = docStyle.getPropertyValue('--chart-color-yellow').trim();
    const squaredDifferenceAreaFill = addOKLCHOpacity(squaredDifferenceLineColor, 0.1);
    const textColor = docStyle.getPropertyValue('--chart-text').trim();
    const gridColor = docStyle.getPropertyValue('--chart-grid').trim();
    const meanLineColor = docStyle.getPropertyValue('--chart-color-orange').trim();
    const dataPointColor = docStyle.getPropertyValue('--chart-color-red').trim();

    return {
      title: 'Mean Minimizes Sum of Squared Differences',
      width,
      height,
      scales: {
        x: {
          time: false,
        },
        y: {
          range: (u, dataMin, dataMax) => {
            return [Y_MIN, dataMax];
          }
        }
      },
      cursor: {
        drag: {
          setScale: false,
          x: false,
          y: false,
        }
      },
      series: [
        {},
        {
          label: 'Sum Squared Distance',
          stroke: squaredDifferenceLineColor,
          fill: squaredDifferenceAreaFill,
          width: 2,
          value: (u, v, seriesIdx, idx) => { return idx == null ? '' : v.toFixed(2) },
        },
        {
          label: 'Data',
          stroke: dataPointColor,
          fill: dataPointColor,
          width: 0,
          points: {
            show: true,
            size: POINT_RADIUS * 2,
            fill: dataPointColor,
          },
          class: 'circle-legend-mark',
          value: (u, v, seriesIdx, idx) => {
            if (idx == null) return '';
            const xVal = u.data[0][idx];
            const closestPoint = draggablePoints.reduce((prev, curr) =>
              Math.abs(curr - xVal) < Math.abs(prev - xVal) ? curr : prev
            );
            return closestPoint.toFixed(2);
          },
        },
      ],
      axes: [
        {
          stroke: textColor,
          grid: {
            stroke: gridColor,
          },
        },
        {
          stroke: textColor,
          grid: {
            stroke: gridColor,
          },
        },
      ],
      hooks: {
        init: [
          (u) => initLegendMean(u, draggablePoints),
        ],
        draw: [
          (u) => {
            // Draw vertical mean line
            const meanValue = mean(draggablePoints);
            const ctx = u.ctx;
            const meanX = u.valToPos(meanValue, 'x', true);
            const plotTop = u.bbox.top;
            const plotBottom = u.bbox.top + u.bbox.height;

            ctx.save();
            ctx.strokeStyle = meanLineColor;
            ctx.lineWidth = 2 * devicePixelRatio;
            ctx.setLineDash([10, 5]);
            ctx.beginPath();
            ctx.moveTo(meanX, plotTop);
            ctx.lineTo(meanX, plotBottom);
            ctx.stroke();
            ctx.restore();
          },
          (u) => drawLegendMean(u, draggablePoints),
        ]
      }
    };
  }

  function setupMeanDragHandlers(chart: uPlot) {
    setupDragHandlers({
      uPlotInstance: chart,
      draggablePoints,
      draggedPointIndex,
      pointRadius: POINT_RADIUS,
      xMin: X_MIN,
      xMax: X_MAX,
      dragBuffer: DRAG_BUFFER,
      onPointsChanged: (points) => {
        data = generateChartData(points);
        chart.setData(data);
        chart.redraw();
      }
    });
  }

  const container = document.getElementById('mean-chart');
  if (!container) {
    console.error('Chart container #mean-chart not found');
    return;
  }
  const { width, height } = getChartDimensions(container, MIN_HEIGHT, ASPECT_RATIO);
  const opts = createChartOptions(width, height);
  const chartRef = { current: new uPlot(opts, data, container) as uPlot | null };
  setupMeanDragHandlers(chartRef.current!);
  setupChartObservers(
    container,
    chartRef,
    MIN_HEIGHT,
    ASPECT_RATIO,
    createChartOptions,
    () => generateChartData(draggablePoints),
    setupMeanDragHandlers
  );
}

// Median plot implementation
function initMedianChart() {
  // Plot-specific constants
  const Y_MIN = -5;
  const SAMPLE_STEP = 0.1;
  const X_MIN = -10.5;
  const X_MAX = 10.5;
  const DRAG_BUFFER = 0.5;
  const POINT_RADIUS = 8;
  const MIN_HEIGHT = 250;
  const ASPECT_RATIO = 16 / 9;

  // Draggable points state - even number for median
  const draggablePoints = [-3, 0, 2, 5];
  const draggedPointIndex = { current: null as number | null };

  function generateChartData(points: number[]): AlignedData {
    const xValues: number[] = [];
    const yValues: number[] = [];
    const dataPointValues: (number | null)[] = [];
    const medianRangeValues: (number | null)[] = [];

    for (let x = X_MIN; x <= X_MAX; x += SAMPLE_STEP) {
      xValues.push(x);
      const yVal = sumAbsDistance(x, points);
      yValues.push(yVal);

      const isDataPoint = points.some(dp => Math.abs(x - dp) < SAMPLE_STEP / 2);
      dataPointValues.push(isDataPoint ? 0 : null);

      // Dummy series for the median range (rendered via paths function)
      medianRangeValues.push(null);
    }

    return [xValues, yValues, dataPointValues, medianRangeValues];
  }

  let data = generateChartData(draggablePoints);

  function createChartOptions(width: number, height: number): uPlot.Options {
    const docStyle = window.getComputedStyle(document.documentElement);
    const absDifferenceLineColor = docStyle.getPropertyValue('--chart-color-yellow').trim();
    const absDifferenceAreaFill = addOKLCHOpacity(absDifferenceLineColor, 0.1);
    const textColor = docStyle.getPropertyValue('--chart-text').trim();
    const gridColor = docStyle.getPropertyValue('--chart-grid').trim();
    const medianRangeColor = addOKLCHOpacity(docStyle.getPropertyValue('--chart-color-orange').trim(), 0.15);
    const dataPointColor = docStyle.getPropertyValue('--chart-color-red').trim();

    return {
      title: 'Median Minimizes Sum of Absolute Differences',
      width,
      height,
      scales: {
        x: {
          time: false,
        },
        y: {
          range: (u, dataMin, dataMax) => {
            return [Y_MIN, dataMax];
          }
        }
      },
      cursor: {
        drag: {
          setScale: false,
          x: false,
          y: false,
        }
      },
      series: [
        {},
        {
          label: 'Sum Absolute Distance',
          stroke: absDifferenceLineColor,
          fill: absDifferenceAreaFill,
          width: 2,
          value: (u, v, seriesIdx, idx) => { return idx == null ? '' : v.toFixed(2) },
        },
        {
          label: 'Data',
          stroke: dataPointColor,
          fill: dataPointColor,
          width: 0,
          points: {
            show: true,
            size: POINT_RADIUS * 2,
            fill: dataPointColor,
          },
          class: 'circle-legend-mark',
          value: (u, v, seriesIdx, idx) => {
            if (idx == null) return '';
            const xVal = u.data[0][idx];
            const closestPoint = draggablePoints.reduce((prev, curr) =>
              Math.abs(curr - xVal) < Math.abs(prev - xVal) ? curr : prev
            );
            return closestPoint.toFixed(2);
          },
        },
        {
          label: 'Median',
          stroke: 'transparent',
          width: 0,
          fill: medianRangeColor,
          paths: (u) => {
            // Custom path drawing for the median range band
            const [medianMin, medianMax] = median(draggablePoints);
            const minX = u.valToPos(medianMin, 'x', true);
            const maxX = u.valToPos(medianMax, 'x', true);
            const topY = u.valToPos(u.scales.y.max!, 'y', true);
            const bottomY = u.valToPos(u.scales.y.min!, 'y', true);

            const path = new Path2D();
            path.rect(minX, topY, maxX - minX, bottomY - topY);

            return {
              stroke: new Path2D(),
              fill: path,
            };
          },
          value: (_u, _v, _seriesIdx, idx) => {
            if (idx == null) return '';
            const [medianMin, medianMax] = median(draggablePoints);
            return medianMin === medianMax
              ? medianMin.toFixed(2)
              : `[${medianMin.toFixed(2)}, ${medianMax.toFixed(2)}]`;
          },
        },
      ],
      axes: [
        {
          stroke: textColor,
          grid: {
            stroke: gridColor,
          },
        },
        {
          stroke: textColor,
          grid: {
            stroke: gridColor,
          },
        },
      ],
    };
  }

  function setupMedianDragHandlers(chart: uPlot) {
    setupDragHandlers({
      uPlotInstance: chart,
      draggablePoints,
      draggedPointIndex,
      pointRadius: POINT_RADIUS,
      xMin: X_MIN,
      xMax: X_MAX,
      dragBuffer: DRAG_BUFFER,
      onPointsChanged: (points) => {
        data = generateChartData(points);
        chart.setData(data);
        chart.redraw();
      }
    });
  }

  const container = document.getElementById('median-chart');
  if (!container) {
    console.error('Chart container #median-chart not found');
    return;
  }
  const { width, height } = getChartDimensions(container, MIN_HEIGHT, ASPECT_RATIO);
  const opts = createChartOptions(width, height);
  const chartRef = { current: new uPlot(opts, data, container) as uPlot | null };
  setupMedianDragHandlers(chartRef.current!);
  setupChartObservers(
    container,
    chartRef,
    MIN_HEIGHT,
    ASPECT_RATIO,
    createChartOptions,
    () => generateChartData(draggablePoints),
    setupMedianDragHandlers
  );
}

// main: init charts
initMeanChart();
initMedianChart();
